import { ClientError, ClientErrorType, httpStatusOfError } from '@edgebox/data-definition-kit';
import { IGovernancePromptMonitoringStatsCombinedFilters, OpenApi } from '@edgebox/sync-core-rest-client';

/**
 * The order in which the sections of an analytics screen ask Sync Core for their statistics: the
 * figures at the top of the screen first, the sections further down after them. On AI Visibility, the
 * prompt list behind the Prompt filter is needed by nothing but the filter, so it waits behind every
 * section. Each screen has its own members, top down; two screens
 * never show at once, so their values may coincide.
 */
export enum StatsRequestPriority {
  Kpis = 0,
  Landscape = 1,
  Trends = 2,
  Attributes = 3,
  Sources = 4,
  Breakdown = 5,
  SourceTrends = 6,
  PromptFilter = 7,

  ContentHealthKpis = 0,
  ContentHealthTrends = 1,
  ContentHealthGaps = 2,
  ContentHealthQualityIssues = 3,
  ContentHealthCoverage = 4,
  ContentHealthInsights = 5,
}

/**
 * How many requests for what the screen shows now are sent at once. Each one is a set of
 * aggregations over every answer of the purpose, so a screen that fires all of its sections
 * together saturates Sync Core and none of them comes back; two at a time keeps the service
 * responsive while the screen fills top down. A superseded request asked for again while still on
 * its way holds such a place again, even beyond this count: it is at Sync Core already. It gives
 * that place up to a waiting request further up the screen whenever that lets the waiting one
 * start while Sync Core has room.
 */
export const MAX_STATS_REQUESTS_IN_FLIGHT = 2;

/**
 * The hard bound on how many of these requests this frame has at Sync Core at once, whatever they
 * are for. Nothing is sent while this many are out.
 *
 * What counts against which bound:
 * - A request somebody still wants counts against both bounds.
 * - A superseded request — its section was left or its filter changed — cannot be called back once
 *   sent: Sync Core works on it to the end. It counts against this bound
 *   only, until it settles, so the current view's requests are sent beside it.
 * - A background request (the prompt list) counts against this bound only, and is sent only once
 *   nothing else is waiting.
 */
export const MAX_STATS_REQUESTS_AT_SYNC_CORE = 4;

/**
 * How long nothing new is sent after a request was superseded. A reader clicking through filters
 * supersedes each filter's requests before they would have been sent, so none of the filters in
 * between reaches Sync Core and none of them holds a place there.
 */
export const STATS_REQUEST_QUIET_PERIOD_MS = 300;

/**
 * How long nothing is sent after a request joined a combined request still waiting, so the rest of
 * its wave joins it too. A screen's sections ask over several renders — one once it has read its
 * lists, one once it comes into view, each a moment after the section above it — so every request
 * asked within this long of the last one travels in the same combined request, and each one that
 * joins starts the wait again.
 */
export const STATS_REQUEST_GATHER_MS = 250;

/**
 * The longest a wave waits for more requests to join, from the moment it began gathering: requests
 * that keep asking never hold back the ones asked before them any longer than this.
 */
export const STATS_REQUEST_MAX_GATHER_MS = 1_000;

/**
 * The longest a hold holds the queue back (`holdStatsRequests`). The lists AI Visibility holds it for
 * answer within about 3.3 s on stage; a hold still in force well past that waits on a list or lookup
 * that is slow or will never answer, and no list may hold back every figure of a screen, so the hold
 * then ends by itself and what waited is sent.
 */
export const STATS_REQUEST_MAX_HOLD_MS = 5_000;

/**
 * How long nothing is sent after a request failed because Sync Core did not answer it: a 5xx, or
 * no response at all. A Sync Core that is failing or restarting answers every request sent into it
 * the same way, and each one adds to the load it is failing under, so the sections still waiting
 * wait until it had time to recover.
 */
export const STATS_REQUEST_FAILURE_PAUSE_MS = 20_000;

/**
 * The longest pause. A request sent after a pause that fails the same way doubles the next pause, up
 * to this; any request that succeeds brings it back to `STATS_REQUEST_FAILURE_PAUSE_MS`.
 */
export const STATS_REQUEST_MAX_FAILURE_PAUSE_MS = 120_000;

/**
 * The most aspects Sync Core's combined route answers in one request; it refuses more. The rest wait
 * for the next combined request.
 */
export const MAX_ASPECTS_PER_COMBINED_REQUEST = 20;

const SERVER_ERROR_STATUS_MIN = 500;
const SERVER_ERROR_STATUS_MAX = 599;
/** What a Sync Core without the combined route answers a combined request with: the route is unknown. */
const ROUTE_NOT_FOUND_STATUS = 404;

/** The kind of client error each refusal status stands for, as the REST client reads a refused response; any other is `Other`. */
const CLIENT_ERROR_TYPE_OF_STATUS: Record<number, ClientErrorType> = {
  400: ClientErrorType.Validation,
  401: ClientErrorType.Unauthorized,
  403: ClientErrorType.Forbidden,
  404: ClientErrorType.NotFound,
};

/** The rejection of a request that was superseded before it was sent: it never reached Sync Core. */
export class StatsRequestSupersededError extends Error {
  constructor() {
    super('The statistics request was superseded before it was sent.');
    this.name = 'StatsRequestSupersededError';
  }
}

/**
 * The identity of a request: the client method and everything it is called with. Two sections, or
 * one section asked again for an answer already on its way, share the one request.
 */
export function statsRequestKey({ method, parameters }: { method: string; parameters: readonly unknown[] }): string {
  return JSON.stringify([method, ...parameters]);
}

/**
 * A statistics request that can travel with others as one aspect of a combined request: the
 * requests waiting with equal shared filters are sent together, and each gets its own item back.
 */
export interface ICombinableStatsRequest {
  /** The filters every aspect of the combined request shares. Requests whose shared filters are equal travel together. */
  sharedFilters: IGovernancePromptMonitoringStatsCombinedFilters;
  /** The request as one aspect of a combined request, its id aside. */
  aspect: Omit<OpenApi.GovernancePromptMonitoringStatsAspectRequest, 'id'>;
  /** The request's answer, read from its item of the combined response. */
  answerOf: (item: OpenApi.GovernancePromptMonitoringStatsCombinedItemResponse) => unknown;
  /** Sends one combined request. */
  sendCombined: (
    filters: IGovernancePromptMonitoringStatsCombinedFilters,
    aspects: OpenApi.GovernancePromptMonitoringStatsAspectRequest[]
  ) => Promise<OpenApi.GovernancePromptMonitoringStatsCombinedResponse>;
}

/** One caller-facing request that travels in a combined request (`IStatsRequest.members`). */
interface ICombinedMember {
  key: string;
  priority: number;
  combinable: ICombinableStatsRequest;
  /** Its own route, used when it is the only one of its combined request. */
  run: () => Promise<unknown>;
  /** No caller shows its failure (`scheduleStatsRequest`'s `silent`); one caller that does makes it false. */
  silent: boolean;
  liveCallers: number;
  settled: boolean;
  unit: IStatsRequest;
  promise: Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}

interface IStatsRequest {
  key: string;
  priority: number;
  sequence: number;
  background: boolean;
  state: 'waiting' | 'running' | 'settled';
  /**
   * The requests travelling in it when it is a combined request, keyed by the shared filters in
   * `groupKey`; each settles on its own. Undefined for a request of its own.
   */
  members?: ICombinedMember[];
  groupKey?: string;
  /** The callers whose signal has not been aborted. */
  liveCallers: number;
  /** Whether it holds one of the places for the current view. */
  current: boolean;
  /** Asked for again after it was superseded while on its way. */
  pickedUp: boolean;
  /** Gave its place for the current view to a request further up the screen; it never takes one again. */
  handedDown: boolean;
  /** How many pauses had begun when it was sent (`pausesBegun`). */
  sentAfterPauses: number;
  /**
   * What it says about Sync Core was learnt while it ran: a combined response, learnt from aspect by
   * aspect (`runCombined`). Settling it teaches nothing more.
   */
  outcomeLearnt: boolean;
  run: () => Promise<unknown>;
  promise: Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}

const requests = new Map<string, IStatsRequest>();
/** The requests travelling in a combined request, by their own key, until they settle. */
const combinedMembers = new Map<string, ICombinedMember>();
let currentInFlight = 0;
let totalInFlight = 0;
let sequence = 0;
let lastSupersededAt = Number.NEGATIVE_INFINITY;
/** Nothing is sent before this moment (`now()`), unless the reader asks a section to try again. */
let pausedUntil = Number.NEGATIVE_INFINITY;
/** The length of the last pause; 0 while nothing failed since the last success. */
let lastPauseMs = 0;
/**
 * How many pauses have begun. A request sent before the last one began that fails too is part of the
 * failure that began it, and does not begin or lengthen another.
 */
let pausesBegun = 0;
/**
 * Whether the Sync Core this frame talks to answered a combined request as an unknown route: a
 * deployment older than the combined route, since each deployment upgrades on its own schedule. From
 * then on every request goes by its own route, as it did before the combined route existed.
 */
let combinedRouteMissing = false;
/**
 * When a request last joined a combined request still waiting, or the last hold ended (`gather`): the
 * wave gathers until `STATS_REQUEST_GATHER_MS` after it.
 */
let lastGatheredAt = Number.NEGATIVE_INFINITY;
/** When the wave gathering now began: it stops gathering `STATS_REQUEST_MAX_GATHER_MS` after this at the latest. */
let gatheringSince = Number.NEGATIVE_INFINITY;
/** How many holds are in force (`holdStatsRequests`). */
let holds = 0;

/** A clock that never steps back, so a changed system time cannot stretch the quiet period. */
function now(): number {
  return performance.now();
}
let startScheduled = false;
let wakeTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Starts what may start, once the code that asked has finished. React runs every cleanup of a
 * render, then every effect, the sections before the screen that holds them; deciding only after
 * all of them have asked is what keeps the figures at the top first in line.
 */
function scheduleStart(): void {
  if (startScheduled) {
    return;
  }
  startScheduled = true;
  queueMicrotask(() => {
    startScheduled = false;
    startNext();
  });
}

/** The moment the wave gathering now stops gathering: nothing is sent before it. */
function gatheredAt(): number {
  return Math.min(lastGatheredAt + STATS_REQUEST_GATHER_MS, gatheringSince + STATS_REQUEST_MAX_GATHER_MS);
}

/**
 * A request joined a combined request still waiting, or the last hold ended: nothing is sent until
 * the rest of the wave had its `STATS_REQUEST_GATHER_MS` to join. A wave that had stopped gathering
 * begins again, and so does one gathering `afresh`, however long its requests waited already.
 */
function gather({ afresh = false }: { afresh?: boolean } = {}): void {
  const at = now();
  if (afresh || at >= gatheredAt()) {
    gatheringSince = at;
  }
  lastGatheredAt = at;
}

function startNext(): void {
  // The last hold to end starts what may start itself (`holdStatsRequests`).
  if (holds > 0 && !combinedRouteMissing) {
    return;
  }
  const quietFor = Math.max(lastSupersededAt + STATS_REQUEST_QUIET_PERIOD_MS, pausedUntil, gatheredAt()) - now();
  const waiting = [...requests.values()]
    .filter((request) => request.state === 'waiting')
    .sort((left, right) => left.priority - right.priority || left.sequence - right.sequence);
  if (!waiting.length) {
    return;
  }
  if (quietFor > 0) {
    if (wakeTimer === undefined) {
      wakeTimer = setTimeout(() => {
        wakeTimer = undefined;
        startNext();
      }, quietFor);
    }
    return;
  }

  for (const request of waiting) {
    if (totalInFlight >= MAX_STATS_REQUESTS_AT_SYNC_CORE) {
      return;
    }
    if (!request.background && currentInFlight >= MAX_STATS_REQUESTS_IN_FLIGHT) {
      // The places for the current view are taken — and can be over-full, because a section picked
      // back up while on its way holds a place beyond the count. Requests picked back up that sit
      // further down the screen hand their places to this one, but only when that leaves a place
      // free for it, and they run on beside it: picking a section back up never puts it ahead of
      // the figures above it while Sync Core has room. A request the queue sent itself got its
      // place in priority order and keeps it, and nothing is sent while the places stay full, so
      // the screen still loads two at a time.
      const displaced = pickedUpToDisplaceFor(request);
      if (!displaced) {
        return;
      }
      for (const handedDown of displaced) {
        handedDown.current = false;
        handedDown.handedDown = true;
        currentInFlight -= 1;
      }
    }
    start(request);
  }
}

/**
 * The requests picked back up whose places for the current view free one for `request`, the one
 * furthest down the screen first — or undefined when handing places down would not free one:
 * what holds them then is a request the queue sent itself, or one no further down than `request`.
 */
function pickedUpToDisplaceFor(request: IStatsRequest): IStatsRequest[] | undefined {
  const pickedUp = [...requests.values()]
    .filter((candidate) => candidate.state === 'running' && candidate.current && candidate.pickedUp)
    .sort((left, right) => right.priority - left.priority);
  const displaced: IStatsRequest[] = [];
  let places = currentInFlight;
  for (const candidate of pickedUp) {
    if (places < MAX_STATS_REQUESTS_IN_FLIGHT) {
      break;
    }
    if (candidate.priority <= request.priority) {
      return undefined;
    }
    displaced.push(candidate);
    places -= 1;
  }
  return places < MAX_STATS_REQUESTS_IN_FLIGHT ? displaced : undefined;
}

/** Whether a failed request means Sync Core did not answer it: a 5xx, or no HTTP status at all. */
function isSyncCoreFailure(error: unknown): boolean {
  const status = httpStatusOfError(error);
  return status === undefined || (status >= SERVER_ERROR_STATUS_MIN && status <= SERVER_ERROR_STATUS_MAX);
}

/**
 * What a request's outcome says about Sync Core: learnt as it settles, or, for a combined response,
 * aspect by aspect as the response is read (`runCombined`). Any success brings the next pause back to
 * its first length, without ending the pause under way. A failure Sync Core did not answer begins a pause,
 * twice as long as the last one when no success came in between — unless the request was sent
 * before the last pause began: that pause already stands for it.
 */
function learnFrom(request: IStatsRequest, failure?: { error: unknown }): void {
  if (!failure) {
    lastPauseMs = 0;
    return;
  }
  if (request.sentAfterPauses !== pausesBegun || !isSyncCoreFailure(failure.error)) {
    return;
  }
  lastPauseMs = lastPauseMs ? Math.min(lastPauseMs * 2, STATS_REQUEST_MAX_FAILURE_PAUSE_MS) : STATS_REQUEST_FAILURE_PAUSE_MS;
  pausesBegun += 1;
  pausedUntil = now() + lastPauseMs;
}

function start(request: IStatsRequest): void {
  request.state = 'running';
  request.sentAfterPauses = pausesBegun;
  totalInFlight += 1;
  if (!request.background) {
    request.current = true;
    currentInFlight += 1;
  }

  let sent: Promise<unknown>;
  try {
    sent = request.run();
  } catch (error) {
    sent = Promise.reject(error);
  }

  const settle = (failure?: { error: unknown }) => {
    request.state = 'settled';
    requests.delete(request.key);
    if (request.current) {
      request.current = false;
      currentInFlight -= 1;
    }
    totalInFlight -= 1;
    if (!request.outcomeLearnt) {
      learnFrom(request, failure);
    }
    scheduleStart();
  };
  sent.then(
    (value) => {
      settle();
      request.resolve(value);
    },
    (error: unknown) => {
      settle({ error });
      request.reject(error);
    }
  );
}

/** One caller no longer wants the answer; the request is superseded once nobody does. */
function leave(request: IStatsRequest): void {
  if (request.state === 'settled') {
    return;
  }
  request.liveCallers -= 1;
  if (request.liveCallers > 0) {
    return;
  }

  lastSupersededAt = now();
  if (request.state === 'waiting') {
    requests.delete(request.key);
    request.state = 'settled';
    request.reject(new StatsRequestSupersededError());
  } else if (request.current) {
    request.current = false;
    currentInFlight -= 1;
  }
  scheduleStart();
}

/**
 * One caller of a request travelling in a combined request no longer wants the answer. Once nobody
 * does, a combined request still waiting goes without it — the request rejects as superseded — and
 * one already sent carries it on to the end, as a request of its own would be.
 */
function leaveMember(member: ICombinedMember): void {
  if (member.settled) {
    return;
  }
  member.liveCallers -= 1;
  if (member.liveCallers > 0) {
    return;
  }

  const unit = member.unit;
  lastSupersededAt = now();
  if (unit.state === 'waiting' && unit.members) {
    unit.members = unit.members.filter((candidate) => candidate !== member);
    // It waits in the place of the request furthest up the screen still in it.
    if (unit.members.length) {
      unit.priority = Math.min(...unit.members.map((candidate) => candidate.priority));
    }
    settleMember(member, { error: new StatsRequestSupersededError() });
  }
  leave(unit);
}

function settleMember(member: ICombinedMember, outcome: { value: unknown } | { error: unknown }): void {
  if (member.settled) {
    return;
  }
  member.settled = true;
  if (combinedMembers.get(member.key) === member) {
    combinedMembers.delete(member.key);
  }
  if ('error' in outcome) {
    member.reject(outcome.error);
  } else {
    member.resolve(outcome.value);
  }
}

/** An aspect's own failure, as the REST client reports a refused request: with its HTTP status. */
function aspectFailure(error: OpenApi.GovernancePromptMonitoringStatsCombinedItemErrorResponse): ClientError {
  const type = CLIENT_ERROR_TYPE_OF_STATUS[error.statusCode] ?? ClientErrorType.Other;
  return new ClientError(type, '', error.message, error, error.statusCode, error.error);
}

/**
 * Puts a member of a combined request that went unanswered back in line as a request of its own,
 * sent by its own route in its turn — the turn it was asked in. A member nobody wants any more is not
 * sent again; it settles with `unwanted`.
 */
function waitAlone(member: ICombinedMember, { from, unwanted }: { from: IStatsRequest; unwanted: unknown }): void {
  if (member.settled) {
    return;
  }
  if (member.liveCallers === 0) {
    settleMember(member, { error: unwanted });
    return;
  }
  const alone = newRequest({ key: `alone:${member.key}`, priority: member.priority, background: false, run: () => runCombined(alone) });
  alone.sequence = from.sequence;
  alone.members = [member];
  alone.liveCallers = 1;
  // Its member settles on its own; the request's own outcome is only learnt from.
  alone.promise.catch(() => undefined);
  member.unit = alone;
  requests.set(alone.key, alone);
}

/**
 * Sends a combined request's members and settles each with its own item. A lone member goes by its
 * own route, exactly as it would without combining; an aspect's own failure fails its member alone.
 * What the response says about Sync Core is learnt aspect by aspect, as the same requests of their own
 * would teach it: an aspect answered brings the next pause back to its first length, an aspect Sync
 * Core failed to compute (a 5xx item) begins a pause as that route's own 5xx would, and a refused
 * aspect teaches nothing. When an aspect was answered beside the failed one, the answer is learnt
 * first, so the pause is a first one.
 *
 * When Sync Core does not answer the combined request at all, it fails the members it was sent for —
 * those still wanted that stand furthest up the screen among the ones whose section says it failed —
 * and pauses the queue, as a failed request of its own does. The other members were only travelling
 * with them: they go back in line, each as a request of its own with its own time budget, and are
 * sent after the pause like every request still waiting; a member nobody wants any more is not sent
 * again. A silent member always goes back in line: it has no section to say it failed and no Try
 * again to ask for it anew. A Sync Core without the combined route puts every member back in line
 * that way, without a pause, and nothing is combined again.
 */
async function runCombined(unit: IStatsRequest): Promise<unknown> {
  const members = unit.members ?? [];
  if (members.length === 1) {
    const [only] = members;
    try {
      const value = await only.run();
      settleMember(only, { value });
      return value;
    } catch (error) {
      settleMember(only, { error });
      throw error;
    }
  }

  const aspects = members.map((member, index) => ({ ...member.combinable.aspect, id: String(index) }));
  const [first] = members;
  let response: OpenApi.GovernancePromptMonitoringStatsCombinedResponse | undefined;
  try {
    response = await first.combinable.sendCombined(first.combinable.sharedFilters, aspects);
  } catch (error) {
    if (httpStatusOfError(error) === ROUTE_NOT_FOUND_STATUS) {
      combinedRouteMissing = true;
      members.forEach((member) => waitAlone(member, { from: unit, unwanted: new StatsRequestSupersededError() }));
      // A refusal: it neither pauses the queue nor counts as an answer.
      throw error;
    }
    // Sent for the sections still wanted that stand furthest up the screen and say when they fail:
    // every one at that place. A silent member is never among them, so the failure reaches a section
    // that says so and offers to try again, and the silent one is asked for again after the pause.
    // With no such section wanted, nothing fails: what is wanted waits again, the rest settles unsent.
    const failing = members.filter((member) => member.liveCallers > 0 && !member.silent);
    const top = failing.length ? Math.min(...failing.map((member) => member.priority)) : undefined;
    members.forEach((member) =>
      failing.includes(member) && member.priority === top
        ? settleMember(member, { error })
        : waitAlone(member, { from: unit, unwanted: error })
    );
    throw error;
  }

  let answered = false;
  let computationFailure: ClientError | undefined;
  try {
    const items = Array.isArray(response?.items) ? response.items : [];
    const itemsById = new Map(items.filter((item) => !!item).map((item) => [item.id, item]));
    members.forEach((member, index) => {
      const item = itemsById.get(String(index));
      if (item?.error) {
        const failure = aspectFailure(item.error);
        if (!computationFailure && isSyncCoreFailure(failure)) {
          computationFailure = failure;
        }
        settleMember(member, { error: failure });
        return;
      }
      // Anything but an error item is Sync Core answering, even in a shape this request cannot read,
      // as a route's own answer of an unexpected shape is.
      answered = true;
      if (!item) {
        settleMember(member, { error: new Error('The combined statistics response carried no item for this request.') });
        return;
      }
      try {
        settleMember(member, { value: member.combinable.answerOf(item) });
      } catch (error) {
        settleMember(member, { error });
      }
    });
  } finally {
    // Whatever the response held, every member leaves with an outcome; the ones settled above keep theirs.
    members.forEach((member) => settleMember(member, { error: new Error('The combined statistics response could not be read.') }));
  }
  // Learnt here, aspect by aspect, rather than from the request's own outcome: the answered aspects
  // first, then one Sync Core failed to compute, so the pause it begins after an answer is a first
  // one. Refused aspects teach nothing, as refused requests of their own teach nothing.
  unit.outcomeLearnt = true;
  if (answered) {
    learnFrom(unit);
  }
  if (computationFailure) {
    learnFrom(unit, { error: computationFailure });
  }
  return response;
}

/** The identity of a set of shared filters: equal filters, whatever the order of their fields, are one group. */
function combinedGroupKey(filters: IGovernancePromptMonitoringStatsCombinedFilters): string {
  const entries = Object.entries(filters).filter(([, value]) => value !== undefined);
  return JSON.stringify(entries.sort(([left], [right]) => left.localeCompare(right)));
}

/**
 * Puts a combinable request into the combined request waiting with its shared filters, or into a new
 * one; a caller asking for one already waiting or on its way joins it.
 */
function scheduleCombinedMember({
  key,
  priority,
  run,
  signal,
  combinable,
  silent,
}: {
  key: string;
  priority: StatsRequestPriority;
  run: () => Promise<unknown>;
  signal: AbortSignal;
  combinable: ICombinableStatsRequest;
  silent: boolean;
}): Promise<unknown> {
  let member = combinedMembers.get(key);
  if (member) {
    member.priority = Math.min(member.priority, priority);
    member.unit.priority = Math.min(member.unit.priority, priority);
    // A caller that shows the failure is one the failure can reach.
    member.silent = member.silent && silent;
    pickUp(member.unit);
  } else {
    // Without the combined route every request waits on its own, and goes by its own route.
    const groupKey = combinedRouteMissing ? undefined : combinedGroupKey(combinable.sharedFilters);
    let unit = [...requests.values()].find(
      (candidate) =>
        groupKey !== undefined &&
        candidate.state === 'waiting' &&
        candidate.groupKey === groupKey &&
        (candidate.members?.length ?? 0) < MAX_ASPECTS_PER_COMBINED_REQUEST
    );
    if (!unit) {
      const created = newRequest({
        key: groupKey === undefined ? `alone:${key}` : `combined:${groupKey}:${sequence}`,
        priority,
        background: false,
        run: () => runCombined(created),
      });
      created.members = [];
      created.groupKey = groupKey;
      // Its members settle on their own; the combined request's own outcome is only learnt from.
      created.promise.catch(() => undefined);
      requests.set(created.key, created);
      unit = created;
    }

    let resolve: (value: unknown) => void = () => undefined;
    let reject: (error: unknown) => void = () => undefined;
    const promise = new Promise<unknown>((resolveMember, rejectMember) => {
      resolve = resolveMember;
      reject = rejectMember;
    });
    member = { key, priority, combinable, run, silent, liveCallers: 0, settled: false, unit, promise, resolve, reject };
    unit.members?.push(member);
    unit.priority = Math.min(unit.priority, priority);
    combinedMembers.set(key, member);
    if (groupKey !== undefined) {
      gather();
    }
  }

  const joined = member;
  if (joined.liveCallers === 0) {
    joined.unit.liveCallers += 1;
  }
  joined.liveCallers += 1;
  signal.addEventListener('abort', () => leaveMember(joined), { once: true });
  scheduleStart();
  return joined.promise;
}

/**
 * Ends the pause a failure began, because the reader asked a section to try again: what waits, the
 * section's own request among it, is sent as it would be without a pause — after the quiet period
 * when something was just superseded. The pause length is kept: unless a request succeeded
 * meanwhile, one that fails again begins a longer pause.
 */
export function resumeStatsRequests(): void {
  pausedUntil = Number.NEGATIVE_INFINITY;
  if (wakeTimer !== undefined) {
    clearTimeout(wakeTimer);
    wakeTimer = undefined;
  }
  scheduleStart();
}

/**
 * Holds every request back until the returned release is called. A screen holds the queue while the
 * lists its first figures wait for are still loading: its sections ask as those lists arrive, one
 * after another, and wait together instead of the first to ask starting alone in a free place, so
 * they travel as one combined request. Releasing the last hold starts the wave gathering afresh
 * (`STATS_REQUEST_GATHER_MS`), for the sections that ask a moment after the screen stands. A hold
 * ends by itself once `STATS_REQUEST_MAX_HOLD_MS` is over, released or not. Once the Sync Core is
 * known to lack the combined route, nothing combines and a hold holds nothing back. Calling the
 * release again does nothing.
 */
export function holdStatsRequests(): () => void {
  holds += 1;
  let released = false;
  const release = (): void => {
    if (released) {
      return;
    }
    released = true;
    clearTimeout(expiry);
    holds -= 1;
    // Without the combined route the hold held nothing back, so there is nothing to gather or start.
    if (!holds && !combinedRouteMissing) {
      gather({ afresh: true });
      scheduleStart();
    }
  };
  const expiry = setTimeout(release, STATS_REQUEST_MAX_HOLD_MS);
  return release;
}

/** A request waiting in the queue for its turn to be sent. */
function newRequest({
  key,
  priority,
  background,
  run,
}: {
  key: string;
  priority: number;
  background: boolean;
  run: () => Promise<unknown>;
}): IStatsRequest {
  let resolve: (value: unknown) => void = () => undefined;
  let reject: (error: unknown) => void = () => undefined;
  const promise = new Promise<unknown>((resolveRequest, rejectRequest) => {
    resolve = resolveRequest;
    reject = rejectRequest;
  });
  return {
    key,
    priority,
    sequence: sequence++,
    background,
    state: 'waiting',
    liveCallers: 0,
    current: false,
    pickedUp: false,
    handedDown: false,
    sentAfterPauses: pausesBegun,
    outcomeLearnt: false,
    run,
    promise,
    resolve,
    reject,
  };
}

/**
 * Asked for again while still on its way, as when the reader switches back: it is what the screen
 * shows now, so it holds a place for the current view again — until a request further up the screen
 * needs that place (see `startNext`). One that has handed its place down already runs on beside the
 * others: taking a place back would keep the requests the queue sends waiting behind one that is at
 * Sync Core anyway.
 */
function pickUp(request: IStatsRequest): void {
  if (request.state === 'running' && !request.current && !request.background && !request.handedDown) {
    request.current = true;
    request.pickedUp = true;
    currentInFlight += 1;
  }
}

/**
 * Runs a statistics request once it is its turn: by priority, then in the order it was asked for.
 * The returned promise settles with the request's own result.
 *
 * A request that fails because Sync Core did not answer it pauses the queue: nothing is sent for
 * `STATS_REQUEST_FAILURE_PAUSE_MS`, doubling up to `STATS_REQUEST_MAX_FAILURE_PAUSE_MS` while the
 * requests sent after a pause fail too, and back to the first length once any request succeeds. The
 * requests waiting meanwhile stay pending; `resumeStatsRequests` ends a pause early.
 *
 * `key` names the request (`statsRequestKey`): a caller asking for one that is already waiting or
 * on its way joins it instead of sending it again, and a superseded one it joins holds a place for
 * the current view again.
 *
 * `signal` is aborted when the caller no longer wants the answer — its effect was cleaned up. Once
 * no caller wants it, a request still waiting is dropped and rejects with
 * `StatsRequestSupersededError`; one already sent keeps running and settles as usual, counting
 * against `MAX_STATS_REQUESTS_AT_SYNC_CORE` only.
 *
 * `combinable` lets the request travel with every other combinable request waiting with equal
 * shared filters, up to `MAX_ASPECTS_PER_COMBINED_REQUEST`, as ONE combined request that holds one
 * place; each gets its own item back. Nothing is sent until the requests asked with it had
 * `STATS_REQUEST_GATHER_MS` to join, nor while a screen holds the queue (`holdStatsRequests`). A
 * combined request that holds a single request sends it by its own route. An aspect's own failure
 * rejects its request alone with its HTTP status, and one Sync Core
 * failed to compute (a 5xx) pauses the queue as its own route's 5xx would. A combined
 * request Sync Core does not answer rejects the requests it was sent for, those still wanted furthest
 * up the screen whose sections say they failed, and pauses the queue as any failure Sync Core did not
 * answer does; the others in it wait again, each on its own, a `silent` one always (see
 * `runCombined`).
 */
export function scheduleStatsRequest<T>({
  key,
  priority,
  run,
  signal,
  background = false,
  combinable,
  silent = false,
}: {
  key: string;
  priority: StatsRequestPriority;
  run: () => Promise<T>;
  signal: AbortSignal;
  /** Never holds a place for the current view, and is sent only once nothing else waits. */
  background?: boolean;
  /** Lets the request travel with the others waiting with equal shared filters, as one combined request. */
  combinable?: ICombinableStatsRequest;
  /**
   * Nothing on screen says when it fails, and nothing offers to ask for it again. A combined request
   * Sync Core does not answer never fails it for this caller's sake: it waits again and is sent after
   * the pause. A caller that says when it fails and asks for the same request makes it that caller's
   * request too, which such a failure can reach.
   */
  silent?: boolean;
}): Promise<T> {
  if (signal.aborted) {
    return Promise.reject(new StatsRequestSupersededError());
  }
  if (combinable && !background) {
    // The one member's result, typed as its callers asked for it: they share one key.
    return scheduleCombinedMember({ key, priority, run, signal, combinable, silent }) as Promise<T>;
  }

  let request = requests.get(key);
  if (!request) {
    request = newRequest({ key, priority, background, run });
    requests.set(key, request);
  } else {
    request.priority = Math.min(request.priority, priority);
    pickUp(request);
  }

  const joined = request;
  joined.liveCallers += 1;
  signal.addEventListener('abort', () => leave(joined), { once: true });
  scheduleStart();
  // The one request's result, typed as its callers asked for it: they share one key, and so one
  // method called with the same parameters.
  return joined.promise as Promise<T>;
}
